// ── Helpers ──

function computeStandardTag(level: number, standard: number) {
    const diff = level - standard
    if (diff >= 1) return { label: 'Above Standard', className: 'bg-green-100 text-green-700' }
    if (diff >= -1) return { label: 'Meets Standard', className: 'bg-blue-100 text-blue-700' }
    return { label: 'Below Standard', className: 'bg-red-100 text-red-700' }
}

// ── Component ──

interface MaturityLevelBarProps {
    /** Current maturity level (1–5) */
    level: number | null
    /** Name of the maturity level, e.g. "Defined" */
    levelName?: string | null
    /** Hex color for the level, e.g. "#ef4444" (from DB) */
    color?: string | null
    /** Industry standard level for comparison marker */
    industryStandard?: number | null
    /** Bar track height — defaults to 'h-2' */
    barHeight?: string
    /** Whether to show the standard comparison tag */
    showStandardTag?: boolean
}

const FALLBACK_BAR_COLOR = '#6b7280'

export function MaturityLevelBar({
    level,
    levelName,
    color,
    industryStandard = null,
    barHeight = 'h-2',
    showStandardTag = true,
}: MaturityLevelBarProps) {
    if (level == null) return null

    const barColor = color || FALLBACK_BAR_COLOR

    const standardTag = industryStandard != null && showStandardTag
        ? computeStandardTag(level, industryStandard)
        : null

    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between">
                <span className="text-lg font-bold" style={{ color: barColor }}>
                    Level {level}<span className="text-sm font-normal text-gray-400"> / 5</span>
                </span>
                <div className="flex items-center gap-2">
                    {levelName && (
                        <span className="text-xs text-gray-500">{levelName}</span>
                    )}
                    {standardTag && (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${standardTag.className}`}>
                            {standardTag.label}
                        </span>
                    )}
                </div>
            </div>
            <div className={`relative w-full bg-gray-100 rounded-full ${barHeight}`}>
                <div
                    className={`${barHeight} rounded-full transition-all`}
                    style={{ width: `${(level / 5) * 100}%`, backgroundColor: barColor }}
                />
                {industryStandard != null && (
                    <div
                        className="absolute top-1/2 -translate-y-1/2 w-0 h-0"
                        style={{ left: `${(industryStandard / 5) * 100}%` }}
                        title={`Industry Standard: Level ${industryStandard}`}
                    >
                        <div className="absolute -left-1.5 -top-1.5 w-3 h-3 bg-amber-500 border-2 border-white rounded-sm rotate-45 shadow-sm" />
                    </div>
                )}
            </div>
        </div>
    )
}
